import { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { 
  Building2, 
  Mail, 
  Phone, 
  User, 
  MapPin, 
  Globe,
  FileText,
  Upload,
  Link,
  Home,
  Award,
  Languages,
  ChevronsUpDown,
  Check,
  Target,
  Users,
  Workflow,
  Focus
} from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { LANGUAGES } from "@/lib/constants";
import { NPOProfile } from "@/types/database";
import { useOnboardingConfig } from "./onboarding/OnboardingSteps";

type EnumData = {
  data: {
    sdg: Array<{
      id: string
      value: string
    }>
    opportunityType: string[]
    opportunityBeneficiaryType: string[]
  }
}

interface NPOProfileFormData extends NPOProfile {
  profile_picture?: File;
  languages_supported: string[];
  ecosystem_accreditation: {
    benevity: boolean;
    yourcause: boolean;
    deed: boolean;
    global_giving: boolean;
    give2asia: boolean;
    guidestart: boolean;
    others: string;
  };
  meta: {
    focus_areas: string[];
    beneficiary_types: string[];
    sdg_alignment: string[];
    languages_supported: string[];
    ecosystem_accreditation: {
      benevity: boolean;
      yourcause: boolean;
      deed: boolean;
      global_giving: boolean;
      give2asia: boolean;
      guidestart: boolean;
      others: string;
    };
  };
}

export function NPOSignupForm({ session, profile }: { session: any; profile: any }) {
  const { toast } = useToast();
  const { updateConfig } = useOnboardingConfig();
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [openSDG, setOpenSDG] = useState(false);
  const [openBeneficiary, setOpenBeneficiary] = useState(false);
  const [openCauseArea, setOpenCauseArea] = useState(false);
  const [openFocusArea, setOpenFocusArea] = useState(false);
  const [openLanguages, setOpenLanguages] = useState(false);

  const { data: enumData } = useQuery<EnumData>({
    queryKey: ["enums"],
    queryFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/opportunities/enums`, {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      })
      if (!response.ok) {
        throw new Error("Failed to fetch enums")
      }
      return response.json()
    },
    enabled: !!session?.access_token
  });

  const { data: npoProfile, isLoading: isLoadingProfile, error: profileError } = useQuery({
    queryKey: ["npoProfile"],
    queryFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/me/profile/npo`, {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        console.error("Profile fetch error:", errorData);
        throw new Error(errorData.message || "Failed to fetch NPO profile");
      }
      const data = await response.json();
      return data;
    },
    enabled: !!session?.access_token,
    retry: 1
  });

  const form = useForm<NPOProfileFormData>({
    defaultValues: {
      id: "",
      org_name: "",
      website: "",
      registered_as: "",
      logo_url: "",
      description: "",
      poc_first_name: "",
      poc_last_name: "",
      phone: "",
      email: "",
      address_line1: "",
      address_line2: "",
      state: "",
      city: "",
      country: "",
      zip_code: "",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      slug: "",
      isd_code: "+65",
      meta: {
        focus_areas: [],
        sdg_alignment: [],
        beneficiary_types: [],
        languages_supported: [],
        ecosystem_accreditation: {
          benevity: false,
          yourcause: false,
          deed: false,
          global_giving: false,
          give2asia: false,
          guidestart: false,
          others: ""
        }
      }
    }
  });

  useEffect(() => {
    console.log("Session token:", session?.access_token);
    console.log("NPO Profile data:", npoProfile);
    if (npoProfile?.data) {
      const profile = npoProfile.data;
      console.log("Setting form data with profile:", profile);
      form.reset({
        id: profile.id,
        org_name: profile.org_name,
        website: profile.website || "",
        registered_as: profile.registered_as || "",
        logo_url: profile.logo_url || "",
        description: profile.description || "",
        poc_first_name: profile.poc_first_name,
        poc_last_name: profile.poc_last_name,
        phone: profile.phone,
        email: profile.email,
        address_line1: profile.address_line1,
        address_line2: profile.address_line2 || "",
        city: profile.city,
        state: profile.state,
        country: profile.country,
        zip_code: profile.zip_code,
        created_at: profile.created_at,
        updated_at: profile.updated_at,
        slug: profile.slug,
        isd_code: profile.isd_code,
        meta: {
          focus_areas: profile.meta?.focus_areas || [],
          sdg_alignment: profile.meta?.sdg_alignment || [],
          beneficiary_types: profile.meta?.beneficiary_types || [],
          languages_supported: profile.meta?.languages_supported || [],
          ecosystem_accreditation: {
            benevity: profile.meta?.ecosystem_accreditation?.benevity || false,
            yourcause: profile.meta?.ecosystem_accreditation?.yourcause || false,
            deed: profile.meta?.ecosystem_accreditation?.deed || false,
            global_giving: profile.meta?.ecosystem_accreditation?.global_giving || false,
            give2asia: profile.meta?.ecosystem_accreditation?.give2asia || false,
            guidestart: profile.meta?.ecosystem_accreditation?.guidestart || false,
            others: profile.meta?.ecosystem_accreditation?.others || ""
          }
        }
      });
    }
  }, [npoProfile, form]);

  useEffect(() => {
    if (profileError) {
      toast({
        title: "Error",
        description: "Failed to load NPO profile. Please try again later.",
        variant: "destructive",
      });
    }
  }, [profileError, toast]);

  if (isLoadingProfile) {
    return <div className="flex items-center justify-center p-8">Loading profile...</div>;
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      form.setValue("profile_picture", file);
    }
  };

  const onSubmit = async (data: NPOProfileFormData) => {
    setLoading(true);
    try {
      let profilePictureUrl = "";
      
      if (data.profile_picture && session?.access_token) {
        setIsUploading(true);
        // Upload to Supabase storage
        const fileExt = data.profile_picture.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        const filePath = `${session.user.id}/profile/${fileName}`;
        
        await supabase.auth.setSession({
          access_token: session.access_token,
          refresh_token: session.refresh_token
        });

        // Upload file to Supabase storage
        const { error: uploadError } = await supabase.storage
          .from('nonprofit-cloud')
          .upload(filePath, data.profile_picture);
          
        if (uploadError) throw uploadError;
        
        // Get the public URL of the uploaded file
        const { data: { publicUrl } } = supabase.storage
          .from('nonprofit-cloud')
          .getPublicUrl(filePath);

        profilePictureUrl = publicUrl;
        setIsUploading(false);
      }

      const apiPayload = {
        org_name: data.org_name,
        description: data.description,
        poc_first_name: data.poc_first_name,
        poc_last_name: data.poc_last_name,
        email: data.email,
        phone: data.phone,
        website: data.website || undefined,
        address_line1: data.address_line1,
        address_line2: data.address_line2 || undefined,
        city: data.city,
        state: data.state,
        country: data.country,
        zip_code: data.zip_code,
        isd_code: data.isd_code,
        logo_url: profilePictureUrl || undefined,
        meta: {
          focus_areas: Array.isArray(data.meta?.focus_areas) ? data.meta.focus_areas : [],
          beneficiary_types: Array.isArray(data.meta?.beneficiary_types) ? data.meta.beneficiary_types : [],
          sdg_alignment: Array.isArray(data.meta?.sdg_alignment) ? data.meta.sdg_alignment : [],
          languages_supported: Array.isArray(data.meta?.languages_supported) ? data.meta.languages_supported : [],
          ecosystem_accreditation: {
            benevity: data.meta?.ecosystem_accreditation?.benevity || false,
            yourcause: data.meta?.ecosystem_accreditation?.yourcause || false,
            deed: data.meta?.ecosystem_accreditation?.deed || false,
            global_giving: data.meta?.ecosystem_accreditation?.global_giving || false,
            give2asia: data.meta?.ecosystem_accreditation?.give2asia || false,
            guidestart: data.meta?.ecosystem_accreditation?.guidestart || false,
            others: data.meta?.ecosystem_accreditation?.others || ""
          }
        }
      };

      console.log('Submitting payload:', apiPayload);

      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/me/profile/npo`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`
        },
        body: JSON.stringify(apiPayload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('API Error Response:', errorData);
        throw new Error(errorData.message || 'Failed to update profile');
      }

      const responseData = await response.json();
      console.log('API Success Response:', responseData);
      
      /**
       * Update the onboarding config
       */
      updateConfig({ profile: true })

      toast({
        title: "Success",
        description: responseData.message || "NPO profile updated successfully",
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to update NPO profile",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        {/* Organization Details Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Organization Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="org_name" className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Organization Name
                </Label>
                <Input
                  id="org_name"
                  {...form.register("org_name", { required: "Organization name is required" })}
                  placeholder="Organization Name"
                />
                {form.formState.errors.org_name && (
                  <p className="text-sm text-red-500">{form.formState.errors.org_name.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Organization Logo
                </Label>
                <div className="flex items-center gap-4">
                  <div className="h-[80px] w-[80px] flex-shrink-0 relative bg-gray-50 rounded-lg flex items-center justify-center">
                    {npoProfile?.data?.logo_url ? (
                      <img 
                        src={npoProfile.data.logo_url} 
                        alt="Organization Logo" 
                        className="max-h-full max-w-full h-auto w-auto object-contain p-2"
                      />
                    ) : (
                      <div className="text-gray-400 text-sm">No logo</div>
                    )}
                  </div>
                  <div className="flex-grow">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      ref={fileInputRef}
                    />
                    {isUploading && <p className="text-sm text-blue-500 mt-1">Uploading logo...</p>}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="website" className="flex items-center gap-2">
                  <Link className="h-4 w-4" />
                  Website URL
                </Label>
                <Input
                  id="website"
                  type="url"
                  {...form.register("website")}
                  placeholder="https://example.org"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Organization Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  {...form.register("email", { required: "Organization email is required" })}
                  placeholder="organization@example.com"
                />
                {form.formState.errors.email && (
                  <p className="text-sm text-red-500">{form.formState.errors.email.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="isd_code" className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    ISD Code
                  </Label>
                  <Input
                    id="isd_code"
                    {...form.register("isd_code", { required: "ISD code is required" })}
                    placeholder="+65"
                  />
                  {form.formState.errors.isd_code && (
                    <p className="text-sm text-red-500">{form.formState.errors.isd_code.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    {...form.register("phone", { required: "Phone number is required" })}
                    placeholder="Phone Number"
                  />
                  {form.formState.errors.phone && (
                    <p className="text-sm text-red-500">{form.formState.errors.phone.message}</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="poc_first_name" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Full Name
                </Label>
                <Input
                  id="poc_first_name"
                  {...form.register("poc_first_name", { required: "Full name is required" })}
                  placeholder="Full Name"
                />
                {form.formState.errors.poc_first_name && (
                  <p className="text-sm text-red-500">{form.formState.errors.poc_first_name.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Address Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Address Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="address_line1" className="flex items-center gap-2">
                  <Home className="h-4 w-4" />
                  Address Line 1
                </Label>
                <Input
                  id="address_line1"
                  {...form.register("address_line1", { required: "Address is required" })}
                  placeholder="Address Line 1"
                />
                {form.formState.errors.address_line1 && (
                  <p className="text-sm text-red-500">{form.formState.errors.address_line1.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="address_line2" className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Address Line 2
                </Label>
                <Input
                  id="address_line2"
                  {...form.register("address_line2")}
                  placeholder="Address Line 2"
                />
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city" className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  City
                </Label>
                <Input
                  id="city"
                  {...form.register("city", { required: "City is required" })}
                  placeholder="City"
                />
                {form.formState.errors.city && (
                  <p className="text-sm text-red-500">{form.formState.errors.city.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="state" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  State
                </Label>
                <Input
                  id="state"
                  {...form.register("state", { required: "State is required" })}
                  placeholder="State"
                />
                {form.formState.errors.state && (
                  <p className="text-sm text-red-500">{form.formState.errors.state.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="country" className="flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  Country
                </Label>
                <Input
                  id="country"
                  {...form.register("country", { required: "Country is required" })}
                  placeholder="Country"
                />
                {form.formState.errors.country && (
                  <p className="text-sm text-red-500">{form.formState.errors.country.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip_code" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Postal Code
                </Label>
                <Input
                  id="zip_code"
                  {...form.register("zip_code", { required: "Postal code is required" })}
                  placeholder="Postal Code"
                />
                {form.formState.errors.zip_code && (
                  <p className="text-sm text-red-500">{form.formState.errors.zip_code.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Organization Profile Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Organization Profile</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="description" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Organization Description
              </Label>
              <Textarea
                id="description"
                {...form.register("description")}
                placeholder="Tell us about your organization's mission, vision, and impact..."
                className="min-h-[150px]"
              />
            </div>

            <FormField
              control={form.control}
              name="meta.languages_supported"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Languages className="h-4 w-4" />
                    Languages Supported
                  </FormLabel>
                  <FormControl>
                    <div className="space-y-2">
                      <Popover open={openLanguages} onOpenChange={setOpenLanguages}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={openLanguages}
                            className="w-full justify-between"
                          >
                            {field.value?.length
                              ? `${field.value.length} selected`
                              : "Select languages..."}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0">
                          <Command>
                            <CommandInput placeholder="Search languages..." />
                            <CommandList>
                              <CommandEmpty>No results found.</CommandEmpty>
                              <CommandGroup>
                                {LANGUAGES.map((language) => {
                                  const isSelected = field.value?.includes(language.value);
                                  return (
                                    <CommandItem
                                      key={language.value}
                                      onSelect={() => {
                                        const values = field.value || [];
                                        const newValues = values.includes(language.value)
                                          ? values.filter((v) => v !== language.value)
                                          : [...values, language.value];
                                        field.onChange(newValues);
                                      }}
                                    >
                                      <div
                                        className={cn(
                                          "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                          isSelected
                                            ? "bg-primary text-primary-foreground"
                                            : "opacity-50 [&_svg]:invisible"
                                        )}
                                      >
                                        <Check className="h-4 w-4" />
                                      </div>
                                      <span className="text-sm">{language.label}</span>
                                      {language.nativeName && (
                                        <span className="ml-2 text-sm text-muted-foreground">
                                          ({language.nativeName})
                                        </span>
                                      )}
                                    </CommandItem>
                                  );
                                })}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      {field.value?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {field.value.map((langValue) => {
                            const language = LANGUAGES.find((l) => l.value === langValue);
                            return language && (
                              <Badge key={langValue} variant="secondary">
                                {language.label}
                              </Badge>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Organization Focus Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Organization Focus</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <FormField
              control={form.control}
              name="meta.sdg_alignment"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    SDG Alignment
                  </FormLabel>
                  <FormControl>
                    <div className="space-y-2">
                      <Popover open={openSDG} onOpenChange={setOpenSDG}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={openSDG}
                            className="w-full justify-between"
                          >
                            {field.value?.length
                              ? `${field.value.length} selected`
                              : "Select SDGs..."}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0">
                          <Command>
                            <CommandInput placeholder="Search SDGs..." />
                            <CommandList>
                              <CommandEmpty>No results found.</CommandEmpty>
                              <CommandGroup>
                                {enumData?.data.sdg.map((sdg) => {
                                  const isSelected = field.value?.includes(sdg.id);
                                  return (
                                    <CommandItem
                                      key={sdg.id}
                                      onSelect={() => {
                                        const values = field.value || [];
                                        const newValues = values.includes(sdg.id)
                                          ? values.filter((v) => v !== sdg.id)
                                          : [...values, sdg.id];
                                        field.onChange(newValues);
                                      }}
                                    >
                                      <div
                                        className={cn(
                                          "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                          isSelected
                                            ? "bg-primary text-primary-foreground"
                                            : "opacity-50 [&_svg]:invisible"
                                        )}
                                      >
                                        <Check className="h-4 w-4" />
                                      </div>
                                      <span className="text-sm">{sdg.value}</span>
                                    </CommandItem>
                                  );
                                })}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      {field.value?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {field.value.map((sdgId) => {
                            const sdg = enumData?.data.sdg.find((s) => s.id === sdgId);
                            return sdg && (
                              <Badge key={sdgId} variant="secondary">
                                {sdg.value}
                              </Badge>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="meta.beneficiary_types"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Beneficiary Types
                  </FormLabel>
                  <FormControl>
                    <div className="space-y-2">
                      <Popover open={openBeneficiary} onOpenChange={setOpenBeneficiary}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={openBeneficiary}
                            className="w-full justify-between"
                          >
                            {field.value?.length
                              ? `${field.value.length} selected`
                              : "Select beneficiary types..."}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0">
                          <Command>
                            <CommandInput placeholder="Search beneficiary types..." />
                            <CommandList>
                              <CommandEmpty>No results found.</CommandEmpty>
                              <CommandGroup>
                                {enumData?.data.opportunityBeneficiaryType.map((type) => {
                                  const isSelected = field.value?.includes(type);
                                  return (
                                    <CommandItem
                                      key={type}
                                      onSelect={() => {
                                        const values = field.value || [];
                                        const newValues = values.includes(type)
                                          ? values.filter((v) => v !== type)
                                          : [...values, type];
                                        field.onChange(newValues);
                                      }}
                                    >
                                      <div
                                        className={cn(
                                          "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                          isSelected
                                            ? "bg-primary text-primary-foreground"
                                            : "opacity-50 [&_svg]:invisible"
                                        )}
                                      >
                                        <Check className="h-4 w-4" />
                                      </div>
                                      <span className="text-sm">{type}</span>
                                    </CommandItem>
                                  );
                                })}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      {field.value?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {field.value.map((type) => (
                            <Badge key={type} variant="secondary">
                              {type}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="meta.focus_areas"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Focus className="h-4 w-4" />
                    Focus Areas
                  </FormLabel>
                  <FormControl>
                    <div className="space-y-2">
                      <Popover open={openFocusArea} onOpenChange={setOpenFocusArea}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={openFocusArea}
                            className="w-full justify-between"
                          >
                            {field.value?.length
                              ? `${field.value.length} selected`
                              : "Select focus areas..."}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0">
                          <Command>
                            <CommandInput placeholder="Search focus areas..." />
                            <CommandList>
                              <CommandEmpty>No results found.</CommandEmpty>
                              <CommandGroup>
                                {enumData?.data.opportunityType.map((type) => {
                                  const isSelected = field.value?.includes(type);
                                  return (
                                    <CommandItem
                                      key={type}
                                      onSelect={() => {
                                        const values = field.value || [];
                                        const newValues = values.includes(type)
                                          ? values.filter((v) => v !== type)
                                          : [...values, type];
                                        field.onChange(newValues);
                                      }}
                                    >
                                      <div
                                        className={cn(
                                          "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                          isSelected
                                            ? "bg-primary text-primary-foreground"
                                            : "opacity-50 [&_svg]:invisible"
                                        )}
                                      >
                                        <Check className="h-4 w-4" />
                                      </div>
                                      <span className="text-sm">{type}</span>
                                    </CommandItem>
                                  );
                                })}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      {field.value?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {field.value.map((type) => (
                            <Badge key={type} variant="secondary">
                              {type}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Ecosystem Accreditation Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Ecosystem Accreditation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  Benevity
                </Label>
                <Input
                  type="checkbox"
                  {...form.register("meta.ecosystem_accreditation.benevity")}
                  className="h-5 w-5"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  Yourcause
                </Label>
                <Input
                  type="checkbox"
                  {...form.register("meta.ecosystem_accreditation.yourcause")}
                  className="h-5 w-5"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  Deed
                </Label>
                <Input
                  type="checkbox"
                  {...form.register("meta.ecosystem_accreditation.deed")}
                  className="h-5 w-5"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  Global Giving
                </Label>
                <Input
                  type="checkbox"
                  {...form.register("meta.ecosystem_accreditation.global_giving")}
                  className="h-5 w-5"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  Give2Asia
                </Label>
                <Input
                  type="checkbox"
                  {...form.register("meta.ecosystem_accreditation.give2asia")}
                  className="h-5 w-5"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  Guidestart
                </Label>
                <Input
                  type="checkbox"
                  {...form.register("meta.ecosystem_accreditation.guidestart")}
                  className="h-5 w-5"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="other_accreditation" className="flex items-center gap-2">
                <Award className="h-4 w-4" />
                Other Accreditation
              </Label>
              <Input
                id="other_accreditation"
                {...form.register("meta.ecosystem_accreditation.others")}
                placeholder="Please specify other accreditations..."
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={loading}>
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </Form>
  );
}